// pages/SectionPage.js
// Generic section detail page. Renders the correct sub-component based on
// sectionType: 'table' | 'tree' | 'external'.
// Fetches /api/diSection/{period}/{sectionKey}/{role}/{user} on load.

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePeriod } from '../hooks/usePeriod';
import { useTourDemo } from '../lib/tourDemo';
import { TOUR_REGISTRY } from '../lib/tours';
import TopBar from '../components/TopBar';
import TableSection from './sections/TableSection';
import BillingReviewSection from './sections/BillingReviewSection';
import StaleJobSection from './sections/StaleJobSection';
import StaleClientSection from './sections/StaleClientSection';
import ClientContactSection from './sections/ClientContactSection';
import ClientGroupsSection from './sections/groups/ClientGroupsSection';
import CullClientReviewSection from './sections/CullClientReviewSection';
import BpprReviewSection from './sections/BpprReviewSection';
import ClientDataSection from './sections/ClientDataSection';
import NonstandardJobsSection from './sections/NonstandardJobsSection';
import JobReviewSection from './sections/JobReviewSection';
import QuotedFeeSection from './sections/QuotedFeeSection';
import './SectionPage.css';

// Sections served by api/diJobReview (shared row model: roll + decision).
const JOB_REVIEW_KEYS = new Set(['stale-jobs', 'late-filings']);

export default function SectionPage() {
  const { sectionKey } = useParams();
  const { userRoles, email } = useAuth();
  const { period, periodActive } = usePeriod();
  const demo = useTourDemo();
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // For sections where the user has multiple roles, use the highest-precedence role
  // (the one with the lowest precedence number in the section's role list).
  const [activeRole, setActiveRole] = useState(null);

  const load = useCallback(async () => {
    if (!period || !sectionKey) return;
    if (demo) { setLoading(false); return; }   // tour: section renders over demo data, no fetch
    if (sectionKey === 'bppr-review') { setLoading(false); return; }   // rendered directly below (no DB catalog row)
    if (sectionKey === 'client-data') { setLoading(false); return; }   // own endpoint (diClientData); needs nothing from diSection
    if (sectionKey === 'nonstandard-jobs') { setLoading(false); return; }   // own endpoint (diNonstandardJobs)
    if (JOB_REVIEW_KEYS.has(sectionKey)) { setLoading(false); return; }     // own endpoint (diJobReview)
    if (sectionKey === 'client-quoted-fee') { setLoading(false); return; }  // own endpoint (diQuotedFee)
    setLoading(true);
    setError(null);
    try {
      // Determine which role to fetch as. We'll use the first matching role by precedence.
      // The full role list for this section comes back in the payload.
      const role = activeRole || userRoles[0] || 'viewer';
      const res = await fetch(
        `/api/diSection/${period}/${sectionKey}/${role}/${encodeURIComponent(email)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      setData(payload);

      // Set active role to best matching role the user has for this section
      if (!activeRole && payload.section?.roles) {
        const sectionRoleKeys = payload.section.roles.map(r => r.role);
        const match = sectionRoleKeys.find(r => userRoles.includes(r));
        if (match) setActiveRole(match);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, sectionKey, activeRole, userRoles, email, demo]);

  useEffect(() => { load(); }, [load]);

  // Spotlight tour: render the section directly over its curated demo data —
  // independent of the diSection fetch and period gating — so the tour always
  // has its page (and every data-tour anchor) to walk through. The section
  // component comes from the shared tour registry.
  const DemoTourSection = demo
    ? (TOUR_REGISTRY.find(t => t.sectionKey === sectionKey)?.Component || null)
    : null;
  if (DemoTourSection) {
    return (
      <>
        <TopBar />
        <div className="section-page">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <DemoTourSection />
        </div>
      </>
    );
  }

  // BPPR Review — admin-only section not in the DB catalog, so render it
  // directly (no diSection lookup / period-section gating needed).
  if (sectionKey === 'bppr-review') {
    return (
      <>
        <TopBar />
        <div className="section-page">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <BpprReviewSection />
        </div>
      </>
    );
  }

  // Client Data — Manager/Partner review of each client's PE attributes. It has its
  // own endpoint (diClientData) and needs nothing from diSection, so render it directly.
  if (sectionKey === 'client-data') {
    return (
      <>
        <TopBar />
        <div className="section-page section-page--wide">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <ClientDataSection />
        </div>
      </>
    );
  }

  // Nonstandard Job Names — Manager-only review of open jobs whose names fail the
  // naming checks. Own endpoint (diNonstandardJobs), so render it directly.
  if (sectionKey === 'nonstandard-jobs') {
    return (
      <>
        <TopBar />
        <div className="section-page section-page--wide">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <NonstandardJobsSection />
        </div>
      </>
    );
  }

  // Stale Jobs / Late Filings — Manager-only roll + decision review of open tax
  // jobs. Own endpoint (diJobReview), so render it directly.
  if (JOB_REVIEW_KEYS.has(sectionKey)) {
    return (
      <>
        <TopBar />
        <div className="section-page section-page--wide">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <JobReviewSection sectionKey={sectionKey} />
        </div>
      </>
    );
  }

  // Client Quoted Fee Review — Partner-only quote per prior-year job, written to Knuula. Own
  // endpoint (diQuotedFee), so render it directly.
  if (sectionKey === 'client-quoted-fee') {
    return (
      <>
        <TopBar />
        <div className="section-page section-page--wide">
          <div className="section-page-header">
            <button className="back-btn" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left" /> Dashboard
            </button>
          </div>
          <QuotedFeeSection />
        </div>
      </>
    );
  }

  async function saveEntries(entries) {
    const role = activeRole || userRoles[0] || 'viewer';
    const res = await fetch(
      `/api/diSection/${period}/${sectionKey}/${role}/${encodeURIComponent(email)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      }
    );
    if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`);
    await load(); // refresh
  }

  if (loading) return (
    <><TopBar /><div className="section-page-loading">Loading section...</div></>
  );

  if (error) return (
    <><TopBar /><div className="section-page-error">Error: {error} <button onClick={load}>Retry</button></div></>
  );

  if (!data) return null;

  const { section, rows, periodActive: pa } = data;
  const readOnly = !pa || !periodActive;

  console.log('[SectionPage] Section object:', section);
  console.log('[SectionPage] Checking: section.key=', section?.key, ', section.section_key=', section?.section_key, ', section.sectionKey=', section?.sectionKey);

  // Role selector (if user has multiple applicable roles for this section)
  const sectionRoleKeys = (section?.roles || []).map(r => r.role);
  const myApplicableRoles = sectionRoleKeys.filter(r => userRoles.includes(r));

  // Custom sections render their own title + role selector internally;
  // SectionPage only provides the Dashboard back button for them.
  const CUSTOM_TYPES = new Set(['billing-review', 'stale-job', 'stale-client', 'client-contact', 'groups', 'cull-client']);
  const isCustomType = CUSTOM_TYPES.has(section?.type);

  return (
    <>
      <TopBar />
      <div className="section-page">
        {/* Billing Review draws its own Dashboard button inside its header row, so
            skip this separate row (it only cost vertical space). */}
        {section?.type !== 'billing-review' && (
        <div className="section-page-header">
          <button className="back-btn" onClick={() => navigate('/')}>
            <i className="fas fa-arrow-left" /> Dashboard
          </button>
          {!isCustomType && (
            <div className="section-page-title-block">
              <h2 className="section-page-title">{section?.label || sectionKey}</h2>
              {readOnly && <span className="section-readonly-badge">Read-only</span>}
            </div>
          )}
          {!isCustomType && myApplicableRoles.length > 1 && (
            <div className="role-selector">
              <label>Viewing as:</label>
              {myApplicableRoles.map(r => (
                <button
                  key={r}
                  className={`role-tab ${activeRole === r ? 'active' : ''}`}
                  onClick={() => setActiveRole(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {section?.type === 'table' && (
          <TableSection
            section={section}
            rows={rows}
            activeRole={activeRole}
            readOnly={readOnly}
            onSave={saveEntries}
          />
        )}

        {section?.type === 'billing-review' && (
          <BillingReviewSection />
        )}

        {section?.type === 'stale-job' && (
          <StaleJobSection />
        )}

        {section?.type === 'stale-client' && (
          <StaleClientSection />
        )}

        {section?.type === 'client-contact' && (
          <ClientContactSection />
        )}

        {section?.type === 'groups' && (
          <ClientGroupsSection />
        )}

        {section?.type === 'cull-client' && (
          <CullClientReviewSection />
        )}


        {section?.type === 'tree' && (
          <div className="section-placeholder">
            {/* TODO: render your tree chart component here.
                rows contains the merged PE + audit data with recordKey = node ID.
                Call onSave([{ recordKey, fieldKey, value, isSignoff }]) to log input. */}
            <p>Tree chart section — wire up your tree component here.</p>
            <pre style={{ fontSize: '0.75rem', background: '#f4f4f4', padding: 12, borderRadius: 6, overflow: 'auto' }}>
              {JSON.stringify(rows?.slice(0, 2), null, 2)}
            </pre>
          </div>
        )}

        {section?.type === 'external' && (
          <div className="section-placeholder">
            <p>This section is managed in an external application.</p>
            <a
              href={section?.config?.url || '#'}
              target="_blank"
              rel="noreferrer"
              className="external-open-btn"
            >
              {section?.config?.button_label || 'Open External App'} <i className="fas fa-external-link-alt" />
            </a>
          </div>
        )}
      </div>
    </>
  );
}
